<template>
  <div class="w-full z-10">
    <div v-if="!border" class="relative">
      <div
        ref="imageWrapper"
        class="rounded-full z-10 w-full bg-white aspect-content overflow-hidden relative flex items-center justify-center"
        :class="whiteBorder ? 'border-2 border-white' : ''"
      >
        <img
          v-if="connection.image"
          ref="image"
          class="z-20 opacity-0 absolute"
          :src="connection.image"
          alt="Connection Image"
        />
        <div
          v-else
          class="z-20 w-full h-full absolute bg-white flex items-center justify-center"
        >
          <div class="w-3/4 h-3/4 bg-white text-truegray-700 rounded-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-full h-full"
            >
              <path
                fill-rule="evenodd"
                d="M18.685 19.097A9.723 9.723 0 0 0 21.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 0 0 3.065 7.097A9.716 9.716 0 0 0 12 21.75a9.716 9.716 0 0 0 6.685-2.653Zm-12.54-1.285A7.486 7.486 0 0 1 12 15a7.486 7.486 0 0 1 5.855 2.812A8.224 8.224 0 0 1 12 20.25a8.224 8.224 0 0 1-5.855-2.438ZM15.75 9a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="relative secondary-border"
      :class="[
        connection.local ? 'bg-primary-before' : 'cw-gradient-before',
        connection.disabled ? 'disabled' : '',
      ]"
    >
      <div
        v-if="connection.disabled"
        class="h-full absolute text-red-600 flex items-center justify-center z-40"
        :style="{ width: 'calc(100% + 6px)' }"
      >
        <div class="w-full h-2 bg-red-600 transform rotate-45"></div>
      </div>
      <div
        ref="imageWrapper"
        class="rounded-full z-10 w-full bg-white border-[6px] border-white aspect-content overflow-hidden relative flex items-center justify-center"
      >
        <img
          v-if="connection.image"
          ref="image"
          class="z-20 opacity-0 absolute"
          :src="connection.image"
          alt="Connection Image"
        />
        <div
          v-else
          class="z-20 w-full h-full absolute bg-white flex items-center justify-center"
        >
          <div
            class="w-3/4 h-3/4 bg-white text-truegray-700 rounded-full"
            :class="connection.disabled ? 'opacity-60' : ''"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-full h-full"
            >
              <path
                fill-rule="evenodd"
                d="M18.685 19.097A9.723 9.723 0 0 0 21.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 0 0 3.065 7.097A9.716 9.716 0 0 0 12 21.75a9.716 9.716 0 0 0 6.685-2.653Zm-12.54-1.285A7.486 7.486 0 0 1 12 15a7.486 7.486 0 0 1 5.855 2.812A8.224 8.224 0 0 1 12 20.25a8.224 8.224 0 0 1-5.855-2.438ZM15.75 9a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>

      <img
        v-if="!connection.policeChecked && connection.local"
        class="w-4/12 absolute bottom-[-16%] left-1/2 transform -translate-x-1/2 z-20"
        src="/img/connections/icons/not-police-vetted.png"
        alt="Not police checked"
        title="Not police checked"
      />
      <img
        v-if="connection.policeChecked && connection.local"
        class="w-4/12 absolute bottom-[-16%] left-1/2 transform -translate-x-1/2 z-20"
        src="/img/connections/icons/police-badge.png"
        alt="police checked"
        title="police checked"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent, onMounted, onUnmounted, ref } from 'vue'

export default defineComponent({
  name: 'ImageConnection',
  components: {},

  props: {
    connection: {
      type: Object,
      required: true,
    },

    border: {
      type: Boolean,
      default: true,
    },

    whiteBorder: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const imageWrapper = ref(null)
    const image = ref(null)

    const adjustImageSize = () => {
      if (!image?.value) {
        return
      }

      const imageWrapperDimensions = imageWrapper.value.getBoundingClientRect()
      const img = new Image()
      img.src = image.value?.src

      img.onload = function () {
        if (image?.value) {
          const imageAspectRatio = this.width / this.height
          if (imageAspectRatio >= 1) {
            image.value.style.opacity = `1`
            image.value.style.height = `${imageWrapperDimensions.height}px`
            image.value.style.width = `${
              imageWrapperDimensions.height * imageAspectRatio
            }px`
          } else {
            image.value.style.opacity = `1`
            image.value.style.width = `${imageWrapperDimensions.width}px`
            image.value.style.height = `${
              imageWrapperDimensions.width / imageAspectRatio
            }px`
          }
        }
      }
    }

    onMounted(() => {
      window.addEventListener('resize', adjustImageSize)
      if (image?.value && image?.value?.complete) {
        adjustImageSize()
      } else if (image.value) {
        image.value.addEventListener('load', adjustImageSize)
      }
    })

    onUnmounted(() => {
      window.removeEventListener('resize', adjustImageSize)
    })

    return {
      imageWrapper,
      image,
    }
  },
})
</script>

<style scoped>
img {
  max-width: none;
  max-height: none;
}
.aspect-content::before {
  content: '';
  float: left;
  padding-top: 100%;
}
.aspect-content::after {
  content: '';
  display: table;
  clear: both;
}
.secondary-border {
  width: calc(100% - 5px);
}

.secondary-border::before {
  content: '';
  position: absolute;
  inset: -5px;
  border-radius: 100%;
  z-index: -1;
}

.bg-primary-before::before {
  background: var(--primary);
}

.cw-gradient-before::before {
  background: -webkit-linear-gradient(45deg, var(--cw-700), var(--cw-alt));
}

.cw-gradient-before.disabled::before {
  background: #dc2626;
}
</style>
